import { Graphics, LINE_CAP } from "pixi.js"
import { TextRenderer } from "../text/text_renderer"
import { Fader } from "./fader"

export interface CountdownTimerProps {
    radius?: number
    strokeWidth?: number
    backgroundColor?: number
    textColor?: number
    onComplete?: () => void
}

export class CountdownTimer extends Graphics {
    private radius: number
    private strokeWidth: number
    private backgroundColor: number
    private textColor: number
    private onComplete?: () => void

    private currentTime: number = 0
    private totalTime: number = 0
    private isRunning: boolean = false
    private textDisplay: any
    private pieSlice: Graphics
    private backgroundCircle: Graphics

    // Animation properties
    private lastDisplayTime: number = 0
    private textAnimationStartTime: number = 0
    private isAnimating: boolean = false
    private fader: Fader | null = null

    constructor(props: CountdownTimerProps = {}) {
        super()

        this.radius = props.radius || 50
        this.strokeWidth = props.strokeWidth || 8
        this.backgroundColor = props.backgroundColor || 0x111a16
        this.textColor = props.textColor || 0xffffff
        this.onComplete = props.onComplete

        this.setupTimer()
    }

    // Radius the countdown arc is stroked along (keeps the stroke inside the dial edge)
    private get arcRadius(): number {
        return this.radius - this.strokeWidth / 2
    }

    // Radius of the dark dial face behind the ring + number
    private get faceRadius(): number {
        return this.radius + this.strokeWidth / 2
    }

    private setupTimer() {
        const { arcRadius, faceRadius } = this

        // Dark translucent dial face: adds depth and keeps the number legible
        // over the bright, busy game world behind it.
        this.backgroundCircle = new Graphics()
        this.backgroundCircle.beginFill(this.backgroundColor, 0.62)
        this.backgroundCircle.drawCircle(0, 0, faceRadius)
        this.backgroundCircle.endFill()

        // Empty "groove" the countdown arc drains along
        this.backgroundCircle.lineStyle({ width: this.strokeWidth, color: 0x000000, alpha: 0.3, cap: LINE_CAP.ROUND })
        this.backgroundCircle.drawCircle(0, 0, arcRadius)

        // Thin bright rim for a crisp, finished edge
        this.backgroundCircle.lineStyle({ width: 1.5, color: 0xffffff, alpha: 0.1 })
        this.backgroundCircle.drawCircle(0, 0, faceRadius - 1)

        this.addChild(this.backgroundCircle)

        // Create the depleting countdown arc
        this.pieSlice = new Graphics()
        this.addChild(this.pieSlice)

        // Create text display
        this.textDisplay = new TextRenderer().color(this.textColor).role("heading").bold().align("center").render("0")

        // Center the text
        this.textDisplay.x = -this.textDisplay.width / 2
        this.textDisplay.y = -this.textDisplay.height / 2
        this.addChild(this.textDisplay)

        // Center the entire timer
        this.x = this.radius + this.strokeWidth
        this.y = this.radius + this.strokeWidth
    }

    public start(totalSeconds: number) {
        this.totalTime = totalSeconds
        this.currentTime = totalSeconds
        this.isRunning = true
        this.lastDisplayTime = -1 // Force animation on first display
        this.isAnimating = false

        // Reset alpha and visibility for new timer
        this.alpha = 1.0
        this.visible = true

        this.updateDisplay()
    }

    public reset() {
        this.currentTime = this.totalTime
        this.isRunning = false
        this.lastDisplayTime = -1 // Force animation on next display
        this.isAnimating = false
        // Stop any active fader
        if (this.fader) {
            this.fader.stop(true)
            this.fader = null
        }
        // Reset alpha and visibility
        this.alpha = 1.0
        this.visible = true
        this.updateDisplay()
    }

    public update(deltaTime: number) {
        if (!this.isRunning || this.currentTime <= 0) {
            return
        }

        this.currentTime -= deltaTime / 1000 // Convert milliseconds to seconds

        if (this.currentTime <= 0) {
            this.currentTime = 0
            this.isRunning = false
            this.onComplete?.()
            this.startFadeOut()
        }

        this.updateDisplay()
    }

    private updateDisplay() {
        // Update text with animation
        const displayTime = Math.ceil(this.currentTime)

        // Check if the display time changed (new second)
        if (displayTime !== this.lastDisplayTime) {
            this.lastDisplayTime = displayTime
            this.textDisplay.text = displayTime.toString()
            this.textDisplay.x = -this.textDisplay.width / 2
            this.textDisplay.y = -this.textDisplay.height / 2

            // Start animation for new number
            this.isAnimating = true
            this.textAnimationStartTime = this.currentTime
        }

        // Handle text animation
        if (this.isAnimating) {
            const timeInSecond = this.currentTime - Math.floor(this.currentTime)
            const animationProgress = Math.min(timeInSecond / 0.2, 1) // Animation over 0.2 seconds

            if (timeInSecond < 0.2) {
                // Scale from 80% to 100% over 0.2 seconds
                const scale = 0.8 + 0.2 * animationProgress
                this.textDisplay.scale.set(scale)
                this.textDisplay.alpha = 1.0
            } else if (timeInSecond > 0.9) {
                // Fade out in the last 0.1 seconds
                const fadeProgress = (timeInSecond - 0.9) / 0.1
                this.textDisplay.alpha = 1.0 - fadeProgress
            } else {
                // Normal state
                this.textDisplay.scale.set(1.0)
                this.textDisplay.alpha = 1.0
            }

            // Re-center text after scaling to keep it centered
            this.textDisplay.x = -this.textDisplay.width / 2
            this.textDisplay.y = -this.textDisplay.height / 2
        }

        // Update the countdown arc (a depleting ring gauge)
        this.pieSlice.clear()

        if (this.totalTime <= 0) {
            return
        }

        // Don't show the arc when the countdown reaches 0
        if (displayTime <= 0) {
            return
        }

        const progress = Math.max(0, Math.min(1, this.currentTime / this.totalTime))
        const arcRadius = this.arcRadius

        // Arc sweeps clockwise from 12 o'clock and shrinks back toward it as time drains
        const startAngle = -Math.PI / 2
        const endAngle = startAngle + progress * Math.PI * 2
        const startX = Math.cos(startAngle) * arcRadius
        const startY = Math.sin(startAngle) * arcRadius

        // Smooth green -> amber -> red ramp as time runs out
        const color = this.rampColor(progress)

        // Warm the number toward the ring color as time drains: it stays crisp
        // white while there's plenty of time, then reddens for urgency near zero.
        // Tint is a cheap multiply over the white text, so it never re-rasterizes.
        const warmFactor = Math.pow(1 - progress, 1.6)
        this.textDisplay.tint = this.interpolateColor(0xffffff, color, warmFactor)

        // Final-seconds urgency: brighter, pulsing glow
        const urgent = displayTime <= 5
        const pulse = 0.5 + 0.5 * Math.sin(this.currentTime * 9)
        const haloAlpha = urgent ? 0.22 + 0.3 * pulse : 0.2

        // Soft outer glow (drawn as a fat, faint arc — cheap, no filters)
        this.pieSlice.lineStyle({
            width: this.strokeWidth + (urgent ? 8 : 5),
            color,
            alpha: haloAlpha,
            cap: LINE_CAP.ROUND,
        })
        this.pieSlice.moveTo(startX, startY)
        this.pieSlice.arc(0, 0, arcRadius, startAngle, endAngle)

        // Main arc with rounded caps
        this.pieSlice.lineStyle({
            width: this.strokeWidth,
            color,
            alpha: 1.0,
            cap: LINE_CAP.ROUND,
        })
        this.pieSlice.moveTo(startX, startY)
        this.pieSlice.arc(0, 0, arcRadius, startAngle, endAngle)

        // Bright leading tip at the draining end — reads like a clock hand
        const tipX = Math.cos(endAngle) * arcRadius
        const tipY = Math.sin(endAngle) * arcRadius
        this.pieSlice.lineStyle(0)
        this.pieSlice.beginFill(0xffffff, 0.85)
        this.pieSlice.drawCircle(tipX, tipY, this.strokeWidth * 0.3)
        this.pieSlice.endFill()
    }

    // Green (full) -> amber (half) -> red (empty)
    private rampColor(progress: number): number {
        const green = 0x4ade80
        const amber = 0xfbbf24
        const red = 0xef4444
        if (progress > 0.5) {
            return this.interpolateColor(amber, green, (progress - 0.5) / 0.5)
        }
        return this.interpolateColor(red, amber, progress / 0.5)
    }

    private interpolateColor(color1: number, color2: number, factor: number): number {
        const r1 = (color1 >> 16) & 0xff
        const g1 = (color1 >> 8) & 0xff
        const b1 = color1 & 0xff

        const r2 = (color2 >> 16) & 0xff
        const g2 = (color2 >> 8) & 0xff
        const b2 = color2 & 0xff

        const r = Math.round(r1 + (r2 - r1) * factor)
        const g = Math.round(g1 + (g2 - g1) * factor)
        const b = Math.round(b1 + (b2 - b1) * factor)

        return (r << 16) | (g << 8) | b
    }

    public getCurrentTime(): number {
        return this.currentTime
    }

    public getTotalTime(): number {
        return this.totalTime
    }

    public isActive(): boolean {
        return this.isRunning
    }

    private startFadeOut() {
        // Stop any existing fader
        if (this.fader) {
            this.fader.stop(true)
        }

        // Create new fader for quick fade out (250ms)
        // 250ms / 5ms intervals = 50 steps, 1.0 / 50 = 0.02 per step
        this.fader = new Fader(this, {
            direction: "out",
            fadeInterval: 5, // 5ms intervals
            fadeAlphaDecrement: 0.02, // 0.02 per step = 50 steps = 250ms total
            minAlpha: 0.0,
            releaseCallback: () => {
                this.visible = false
                this.fader = null
            },
        })

        this.fader.update()
    }

    public stop() {
        this.isRunning = false
        // Stop any active fader
        if (this.fader) {
            this.fader.stop(true)
            this.fader = null
        }
    }
}
